import React from "react"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import { styled } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import { PUBLIC } from "constants/index"
import { NAVIGATE_TO_CREATE_ITEM_PAGE, NAVIGATE_TO_CREATE_LIST_PAGE } from "constants/events"
import { useAppSelector } from "appRedux/hooks"
import { resetListFormSlice, prefillListFormFields } from "appRedux/slices/listFormSlice"
import AddIcon from "components/atoms/_icons/AddIcon"
import CTA from "components/atoms/CTA"
import Divider from "components/atoms/Divider"
import Logo from "components/atoms/_icons/Logo"

const TEMPLATE_PLACEHOLDERS: TemplatePlaceholder[] = [
  { key: "birthday" },
  { key: "wedding" },
  { key: "newYear" },
  { key: "home" },
]

type TemplatePlaceholder = {
  key: string;
}

export default function QuickCreateCard() {
  const userItemCount = useAppSelector((state) => state.initSlice?.userInfo?.itemCount)

  const { t } = useTranslation()
  const dispatch = useDispatch()

  function handleCreateNewItem() {
    document.dispatchEvent(new CustomEvent(NAVIGATE_TO_CREATE_ITEM_PAGE))
  }

  function handleCreateNewList() {
    dispatch(resetListFormSlice())
    document.dispatchEvent(new CustomEvent(NAVIGATE_TO_CREATE_LIST_PAGE))
  }

  function handleCreateTemplateList(template: TemplatePlaceholder) {
    dispatch(resetListFormSlice())
    const payload: WishListRequestPayload = { name: t(`feed.quickCreate.templates.${template.key}`), privacy: PUBLIC }
    dispatch(prefillListFormFields(payload))
    document.dispatchEvent(new CustomEvent(NAVIGATE_TO_CREATE_LIST_PAGE))
  }

  return (
    userItemCount === 0 ? <Container>
      <Header>
        <TextWrapper>
          <Title>{t("feed.quickCreate.title")}</Title>
          <Logo/>
        </TextWrapper>
      </Header>

      <ButtonsWrapper>
        <CTA
          variant="contained"
          size="medium"
          endIcon={<AddIcon/>}
          onClick={handleCreateNewItem}
        >
          {t("feed.quickCreate.itemCTALabel")}
        </CTA>

        <CTA
          variant="outlined"
          size="medium"
          endIcon={<AddIcon/>}
          onClick={handleCreateNewList}
        >
          {t("feed.quickCreate.listCTALabel")}
        </CTA>
      </ButtonsWrapper>

      <Divider isFullWidth={true}/>

      <TemplatesSection>
        <TemplatesLabel>{t("feed.quickCreate.startWithTemplate")}</TemplatesLabel>
        <TemplatesRow>
          {TEMPLATE_PLACEHOLDERS.map((template) => (
            <TemplateChip key={template.key} onClick={() => handleCreateTemplateList(template)}>
              <span>{t(`feed.quickCreate.templates.${template.key}`)}</span>
            </TemplateChip>
          ))}
        </TemplatesRow>
      </TemplatesSection>
    </Container> : null
  )
}

const Container = styled(Stack)(({ theme }) => ({
  width: "100%",
  backgroundColor: theme.palette.primary["1"],
  borderRadius: "8px",
  boxShadow: `0 0 10px 0 ${theme.palette.gray["6"]}`,
  padding: "16px",
  gap: "16px",
  marginBottom: "8px"
}))

const Header = styled(Stack)(() => ({
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "12px"
}))

const TextWrapper = styled(Stack)(({ theme }) => ({
  flexDirection:"row",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",

  "& svg": {
    width: "44px",
    height: "44px",

    "& path": {
      fill: theme.palette.primary[6]
    }
  }
}))

const Title = styled(Typography)(({ theme }) => ({
  fontSize: "20px",
  fontWeight: 500,
  color: theme.palette.gray["15"]
}))

const ButtonsWrapper = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  gap: "8px",

  "& svg": {
    width: "24px !important",
    height: "24px !important"
  },

  "& button": {
    width: "fit-content",
    paddingRight: "12px",

    "& span": {
      marginLeft: "0 !important",
      marginRight: "4px !important"
    }
  },

  "& > button:first-of-type svg path": {
    stroke: theme.palette.gray["1"]
  },

  "& > button:not(first-of-type) svg path": {
    stroke: theme.palette.primary["6"]
  }
}))

const TemplatesSection = styled(Stack)(() => ({
  gap: "8px"
}))

const TemplatesLabel = styled(Typography)(({ theme }) => ({
  fontSize: "1.2rem",
  color: theme.palette.gray["13"]
}))

const TemplatesRow = styled(Stack)(() => ({
  flexDirection: "row",
  gap: "8px",
  overflowX: "auto"
}))

const TemplateChip = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  alignItems: "center",
  gap: "4px",
  padding: "4px 12px",
  borderRadius: "128px",
  border: `1px solid ${theme.palette.primary["3"]}`,
  backgroundColor: theme.palette.gray["1"],
  whiteSpace: "nowrap",
  fontSize: "1.1rem",
  color: theme.palette.gray["15"],
  cursor: "pointer",

  "& svg": {
    width: "16px",
    height: "16px"
  }
}))
